/**
 * Out-of-Band Communications Status Indicator
 * 
 * Displays the status of Predator's secure, identity-based communication channels
 * that run separately from main enterprise network traffic.
 * 
 * Features:
 * - Real-time connection status
 * - Channel health metrics
 * - Encryption status
 * - Latency indicators
 * 
 * @version 1.0.0
 * @date January 9, 2026
 * @task T-S21-015
 */

import { useState, useEffect, useMemo } from 'react';
import { 
  Radio, 
  Shield, 
  Lock, 
  Unlock,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Wifi,
  WifiOff,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react';
import { Badge } from './badge';
import { Button } from './button';
import { useQuery } from '@tanstack/react-query';

// ============================================================================
// Types
// ============================================================================

export interface OOBChannelStatus {
  id: string;
  name: string;
  type: 'mdc' | 'ldc' | 'ka' | 'claw';
  status: 'connected' | 'degraded' | 'disconnected' | 'initializing';
  encrypted: boolean;
  latencyMs: number;
  lastHeartbeat: string;
  messagesPerSecond: number;
  errorRate: number;
  endpoint: string;
}

export interface OOBSystemStatus {
  overallStatus: 'healthy' | 'degraded' | 'critical' | 'offline';
  totalChannels: number;
  connectedChannels: number;
  averageLatency: number;
  totalMessagesPerSecond: number;
  channels: OOBChannelStatus[];
  lastUpdated: string;
}

// ============================================================================
// Mock API (Replace with real API)
// ============================================================================

async function fetchOOBStatus(): Promise<OOBSystemStatus> {
  // TODO: Replace with real API call to /api/v1/oob/status
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const channels: OOBChannelStatus[] = [
    {
      id: 'oob-mdc',
      name: 'MDC Primary',
      type: 'mdc',
      status: 'connected',
      encrypted: true,
      latencyMs: 12,
      lastHeartbeat: new Date().toISOString(),
      messagesPerSecond: 145,
      errorRate: 0.001,
      endpoint: 'oob://mdc.predator.internal:8443',
    },
    {
      id: 'oob-ka-us',
      name: 'KA Americas',
      type: 'ka',
      status: 'connected',
      encrypted: true,
      latencyMs: 28,
      lastHeartbeat: new Date().toISOString(),
      messagesPerSecond: 89,
      errorRate: 0.002,
      endpoint: 'oob://ka-us.predator.internal:8443',
    },
    {
      id: 'oob-ka-eu',
      name: 'KA Europe',
      type: 'ka',
      status: 'degraded',
      encrypted: true,
      latencyMs: 156,
      lastHeartbeat: new Date(Date.now() - 30000).toISOString(),
      messagesPerSecond: 34,
      errorRate: 0.05,
      endpoint: 'oob://ka-eu.predator.internal:8443',
    },
    {
      id: 'oob-ldc-1',
      name: 'LDC US-East',
      type: 'ldc',
      status: 'connected',
      encrypted: true,
      latencyMs: 8,
      lastHeartbeat: new Date().toISOString(),
      messagesPerSecond: 234,
      errorRate: 0.0,
      endpoint: 'oob://ldc-us-east.predator.internal:8443',
    },
  ];
  
  const connectedChannels = channels.filter(c => c.status === 'connected').length;
  const degradedChannels = channels.filter(c => c.status === 'degraded').length;
  
  let overallStatus: OOBSystemStatus['overallStatus'] = 'healthy';
  if (degradedChannels > 0) overallStatus = 'degraded';
  if (connectedChannels < channels.length / 2) overallStatus = 'critical';
  if (connectedChannels === 0) overallStatus = 'offline';
  
  return {
    overallStatus,
    totalChannels: channels.length,
    connectedChannels,
    averageLatency: Math.round(channels.reduce((sum, c) => sum + c.latencyMs, 0) / channels.length),
    totalMessagesPerSecond: channels.reduce((sum, c) => sum + c.messagesPerSecond, 0),
    channels,
    lastUpdated: new Date().toISOString(),
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function getStatusColor(status: OOBChannelStatus['status']): string {
  switch (status) {
    case 'connected': return 'text-green-400';
    case 'degraded': return 'text-amber-400';
    case 'disconnected': return 'text-red-400';
    case 'initializing': return 'text-cyan-400';
    default: return 'text-slate-400';
  }
}

function getStatusIcon(status: OOBChannelStatus['status']) {
  switch (status) {
    case 'connected': return <CheckCircle className="h-4 w-4 text-green-400" />;
    case 'degraded': return <AlertTriangle className="h-4 w-4 text-amber-400" />;
    case 'disconnected': return <XCircle className="h-4 w-4 text-red-400" />;
    case 'initializing': return <RefreshCw className="h-4 w-4 text-cyan-400 animate-spin" />;
    default: return <Wifi className="h-4 w-4 text-slate-400" />;
  }
}

function getOverallStatusBadge(status: OOBSystemStatus['overallStatus']) {
  switch (status) {
    case 'healthy':
      return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="h-3 w-3 mr-1" />Healthy</Badge>;
    case 'degraded':
      return <Badge className="bg-amber-500/20 text-amber-400"><AlertTriangle className="h-3 w-3 mr-1" />Degraded</Badge>;
    case 'critical':
      return <Badge className="bg-red-500/20 text-red-400"><XCircle className="h-3 w-3 mr-1" />Critical</Badge>;
    case 'offline':
      return <Badge className="bg-slate-500/20 text-slate-400"><WifiOff className="h-3 w-3 mr-1" />Offline</Badge>;
    default:
      return null;
  }
}

function formatLatency(ms: number): string {
  if (ms < 50) return `${ms}ms`;
  if (ms < 100) return `${ms}ms ⚠️`;
  return `${ms}ms 🔴`;
}

// ============================================================================
// Channel Row Component
// ============================================================================

interface ChannelRowProps {
  channel: OOBChannelStatus;
}

function ChannelRow({ channel }: ChannelRowProps) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
      <div className="flex items-center gap-3">
        {getStatusIcon(channel.status)}
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">{channel.name}</span>
            <Badge variant="outline" className="text-xs">{channel.type.toUpperCase()}</Badge>
          </div>
          <div className="text-xs text-slate-500 font-mono">{channel.endpoint}</div>
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs">
        <div className="text-right">
          <div className={channel.encrypted ? 'text-green-400' : 'text-red-400'}>
            {channel.encrypted ? <Lock className="h-3 w-3 inline mr-1" /> : <Unlock className="h-3 w-3 inline mr-1" />}
            {channel.encrypted ? 'Encrypted' : 'Unencrypted'}
          </div>
          <div className="text-slate-500">{formatLatency(channel.latencyMs)}</div>
        </div>
        <div className="text-right">
          <div className="text-cyan-400">{channel.messagesPerSecond}/s</div>
          <div className={channel.errorRate > 0.01 ? 'text-amber-400' : 'text-slate-500'}>
            {(channel.errorRate * 100).toFixed(2)}% err
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Compact Status Indicator (for header/sidebar)
// ============================================================================

export interface OOBStatusIndicatorProps {
  compact?: boolean;
  className?: string;
}

export function OOBStatusIndicator({ compact = true, className = '' }: OOBStatusIndicatorProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  
  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ['oob', 'status'],
    queryFn: fetchOOBStatus,
    staleTime: 5000,
    refetchInterval: 10000, // Real-time updates every 10 seconds
  });
  
  if (isLoading || !status) {
    return (
      <div className={`flex items-center gap-2 rounded-lg bg-slate-800/50 px-3 py-1.5 ${className}`}>
        <Radio className="h-4 w-4 text-slate-400 animate-pulse" />
        <span className="text-xs text-slate-400">OOB...</span>
      </div>
    );
  }
  
  if (compact) {
    return (
      <button
        onClick={() => setExpanded(!expanded)}
        className={`relative flex items-center gap-2 rounded-lg bg-slate-800/50 px-3 py-1.5 hover:bg-slate-700/50 transition-colors ${className}`}
        title="Out-of-Band Communications Status"
      >
        <Radio className={`h-4 w-4 ${
          status.overallStatus === 'healthy' ? 'text-green-400' :
          status.overallStatus === 'degraded' ? 'text-amber-400' :
          status.overallStatus === 'critical' ? 'text-red-400' :
          'text-slate-400'
        }`} />
        <span className="text-xs text-slate-400">OOB</span>
        <span className={`text-xs font-medium ${
          status.overallStatus === 'healthy' ? 'text-green-400' :
          status.overallStatus === 'degraded' ? 'text-amber-400' :
          status.overallStatus === 'critical' ? 'text-red-400' :
          'text-slate-400'
        }`}>
          {status.connectedChannels}/{status.totalChannels}
        </span>
        
        {/* Expanded dropdown */}
        {expanded && (
          <div 
            className="absolute top-full right-0 mt-2 w-96 rounded-lg border border-slate-700 bg-slate-900 shadow-xl z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-cyan-400" />
                  <span className="font-semibold text-white">Out-of-Band Comms</span>
                </div>
                {getOverallStatusBadge(status.overallStatus)}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-lg font-bold text-white">{status.connectedChannels}</div>
                  <div className="text-xs text-slate-500">Connected</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-cyan-400">{status.averageLatency}ms</div>
                  <div className="text-xs text-slate-500">Avg Latency</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-violet-400">{status.totalMessagesPerSecond}</div>
                  <div className="text-xs text-slate-500">Msgs/sec</div>
                </div>
              </div>
            </div>
            <div className="p-3 max-h-64 overflow-auto">
              {status.channels.map(channel => (
                <ChannelRow key={channel.id} channel={channel} />
              ))}
            </div>
            <div className="p-2 border-t border-slate-700 text-center">
              <span className="text-xs text-slate-500">
                Last updated: {new Date(status.lastUpdated).toLocaleTimeString()}
              </span>
            </div>
          </div>
        )}
      </button>
    );
  }
  
  // Full panel view
  return (
    <div className={`rounded-lg border border-slate-700 bg-slate-800 ${className}`}>
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-cyan-500/20 p-2">
              <Radio className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white">Out-of-Band Communications</h3>
              <p className="text-xs text-slate-400">Secure identity-based channels</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getOverallStatusBadge(status.overallStatus)}
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="mt-4 grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-white">{status.totalChannels}</div>
            <div className="text-xs text-slate-500">Total Channels</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">{status.connectedChannels}</div>
            <div className="text-xs text-slate-500">Connected</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-cyan-400">{status.averageLatency}ms</div>
            <div className="text-xs text-slate-500">Avg Latency</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-violet-400">{status.totalMessagesPerSecond}</div>
            <div className="text-xs text-slate-500">Msgs/sec</div>
          </div>
        </div>
      </div>
      
      <div className="p-4">
        <h4 className="text-sm font-medium text-slate-400 mb-3">Channel Status</h4>
        <div className="space-y-1">
          {status.channels.map(channel => (
            <ChannelRow key={channel.id} channel={channel} />
          ))}
        </div>
      </div>
      
      <div className="px-4 py-2 border-t border-slate-700 text-xs text-slate-500 text-center">
        Last updated: {new Date(status.lastUpdated).toLocaleTimeString()} • Auto-refresh every 10s
      </div>
    </div>
  );
}

export default OOBStatusIndicator;

